/**
 * Camera moves on the workflow canvas: a requestAnimationFrame tween on
 * GTM's power3-in-out curve (cubic-bezier 0.76, 0, 0.24, 1).
 *
 * @module components/workflows/canvas/tween
 */
export interface Tween {
  readonly stop: () => void;
}

function bezier(x1: number, y1: number, x2: number, y2: number) {
  const sample = (a1: number, a2: number, t: number) => ((1 - 3 * a2 + 3 * a1) * t + (3 * a2 - 6 * a1)) * t * t + 3 * a1 * t;
  const slope = (a1: number, a2: number, t: number) => 3 * (1 - 3 * a2 + 3 * a1) * t * t + 2 * (3 * a2 - 6 * a1) * t + 3 * a1;
  return (x: number) => {
    let t = x;
    for (let index = 0; index < 8; index++) {
      const error = sample(x1, x2, t) - x;
      const derivative = slope(x1, x2, t);
      if (Math.abs(error) < 1e-5 || Math.abs(derivative) < 1e-6) break;
      t -= error / derivative;
    }
    return sample(y1, y2, Math.min(1, Math.max(0, t)));
  };
}

export const power3InOut = bezier(0.76, 0, 0.24, 1);

export function animate(duration: number, onUpdate: (progress: number) => void): Tween {
  const start = performance.now();
  let frame = 0;
  const step = (now: number) => {
    const linear = Math.min(1, (now - start) / duration);
    onUpdate(power3InOut(linear));
    if (linear < 1) frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  return { stop: () => cancelAnimationFrame(frame) };
}

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
